export type Testimonial = {
  name: string;
  role: string;
  quote: string;
  rating: number;
  avatar?: string;
};

/** Two-letter fallback shown when a reviewer has no avatar. */
export function getTestimonialInitials(name: string): string {
  const words = name
    .replace(/[^\p{L}\s]/gu, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return '?';
  }
  if (words.length > 1) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
}

/**
 * Real customer reviews, shared by the marketing site and the dashboard's
 * sign-up and in-app pricing pages so every surface reflects the same list:
 * add or edit a review here and it updates everywhere at once.
 *
 * `avatar` paths are served from the marketing app's public directory, so
 * non-marketing consumers must resolve them against `baseUrl.Marketing`
 * (see `resolveTestimonialAvatarSrc` in `./components/testimonial-card`).
 */
export const testimonials: Testimonial[] = [
  {
    name: 'Taniya',
    role: 'Technical Writer at WebsiteLearners',
    rating: 5,
    avatar: '/assets/homepage/testimonials/Taniya.webp',
    quote:
      'Videomule has been a great addition to my scripting workflow! Tasks that use to take me hours, now are done in mere minutes. I especially love how I can edit the script lines right there in the tool, and within a click the voiceover is regenerated.'
  },
  {
    name: 'Sandesh Wagle',
    role: 'Project Manager at Rent Matrix',
    rating: 4,
    avatar: '/assets/homepage/testimonials/Sandesh.webp',
    quote:
      "VideoMule completely changed how I approach video content creation. I'd been stuck for weeks trying to figure out how to build a proper tutorial, and nothing seemed to click until I found VideoMule. What used to feel like an impossible task turned into something I completed in just 5 hours."
  },
  {
    name: 'Shripal',
    role: 'Founder at EasyHR',
    rating: 5,
    avatar: '/assets/homepage/testimonials/Shripal.webp',
    quote:
      "VideoMule is a fantastic application that solves many of the challenges we face with video recording and voiceovers. Since using VideoMule, our video editing time has reduced by 50% or more. The videos also look much more professional, and we've seen significantly better engagement on videos created using VideoMule."
  },
  {
    name: 'Johnson',
    role: 'Demand Gen Lead at MagicPort',
    rating: 5,
    quote:
      'VideoMule allows us to quickly produce high-quality how-to videos for our B2B SaaS. I love how the AI can understand how our product works and its benefits to users.'
  },
  {
    name: 'Farhad',
    role: 'Data Scientist',
    rating: 5,
    quote:
      'I could use VideoMule without spending hours learning the tool. It is intuitive and easy to use, and I created my first video in a short time.'
  },
  {
    name: 'Mohamed Aziz Saouabi',
    role: 'CEO at Firstkm',
    rating: 5,
    quote:
      "When we were preparing our startup grant application, we needed a way to show, not just tell, evaluators what our product actually does. VideoMule AI made that possible. In a fraction of the time it would have taken to script, shoot, and edit a demo ourselves, we had a polished, professional walkthrough of our product ready to embed directly in our application. For any founder prepping a grant or investor application, I'd recommend VideoMule AI without hesitation."
  },
  {
    name: 'Mark',
    role: 'Lead Developer at Korudaz Ltd',
    rating: 5,
    quote:
      'Created some video guides on a new SaaS product, and the narrative to video is excellent. The effort on my part was minimal, and the recordings have been well received. The quality of voice is so important to me and they sound very natural and realistic. 100% recommend Video Mule.'
  },
  {
    name: 'Shylendra T.M.',
    role: 'Co-founder & CTO at mcpcognito',
    rating: 5,
    avatar: '/assets/homepage/testimonials/Shylendra.webp',
    quote:
      "Absolutely love VideoMule! It's one of the best apps I've used for my projects: simple, reliable, and incredibly useful. It has made my workflow so much easier. Highly recommended!"
  },
  {
    name: 'Willie',
    role: 'CEO at USP Designs',
    rating: 5,
    quote:
      'Been using VideoMule for a couple of videos. Their support was fast and resolved my issues quickly. The script created by their AI was very good and I could use the resulting videos nearly as is. Easy to make training videos by doing screen recordings only.'
  },
  {
    name: 'Ronil',
    role: 'Quality Assurance at CAAF',
    rating: 5,
    quote:
      'VideoMule AI is the best video editing AI I have used so far! It is simple, easy to use, and saves so much time. Just upload your video, and the AI does the rest for you. No complicated editing skills needed, just great results with minimal effort.'
  },
  {
    name: 'AjithPrabu',
    role: 'Solopreneur at Worksmarto',
    rating: 5,
    quote:
      "I always wondered if there's a way to sync the voiceover of the script with the tutorial video. But Video Mule comes with the pack: it auto generates an AI script and syncs it with the tutorial. I would say it's a game changer if you want to create tutorial based lessons."
  },
  {
    name: 'Jaco Marais',
    role: 'Developer at MunCalc',
    rating: 4,
    quote:
      "Found you on YouTube and was immediately impressed. Ease of use is second to none and the AI translation was almost perfect. Editing and polishing was also easy. It was one of the simplest methods, and I've tried several well-known apps."
  },
  {
    name: 'Jerry',
    role: 'Designer at Medirevs',
    rating: 5,
    quote:
      'Used Video Mule to create a website demonstration video for our tech startup and it was incredibly helpful and really easy to use. The feature that allows you to create video demonstration videos from screen recording was amazing.'
  },
  {
    name: 'John',
    role: 'General Manager at I-TRX',
    rating: 5,
    quote:
      'It is great for businesses with a smaller budget that want to make advertisements that seem professional. I love how easy it is to edit the text the AI creates. The different voices are also great, so our videos do not seem repetitive.'
  },
  {
    name: 'Eyüp',
    role: 'Developer at Foodord',
    rating: 5,
    quote:
      'I discovered your product on YouTube and loved how simple and intuitive it was from the start. Getting started was quick and straightforward, the onboarding process was clear, and the team was supportive and responsive whenever we had questions.'
  },
  {
    name: 'Tofik',
    role: 'Sales Executive at 365billing',
    rating: 5,
    quote:
      "It's the best software for tutorial making. I've been using it for my tutorial creation and it's a great product. It saves me a lot of time."
  },
  {
    name: 'G.I',
    role: 'CEO at GI 2 Ai',
    rating: 4,
    quote: 'Great platform for demo videos. Highly recommended!'
  }
];

/**
 * A random sample, so a surface shows different reviews on each visit.
 *
 * Callers must pick where this runs: fine in a server component (a fresh
 * sample per request, sent down in the RSC payload) or in a client modal's
 * lazy `useState` initialiser (mounted on interaction, never hydrated), but
 * never during the render of a client component that is server-rendered
 * first, since server and client would draw different samples and mismatch.
 */
export function pickRandomTestimonials(
  count: number,
  list: Testimonial[] = testimonials
): Testimonial[] {
  const pool = [...list];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}
